import React, { useEffect, useMemo, useRef, useState } from "react";
import { HiOutlineBriefcase, HiOutlineUser, HiOutlineUserGroup, HiOutlineViewBoards, } from "react-icons/hi";
import { HiMagnifyingGlass, HiXMark } from "react-icons/hi2";
import { useSelector, useDispatch } from "react-redux";
import { MentionSearchType } from "./types/mentionSearch";
import CustomTooltip from "../common/CustomTooltip";
import { clearMentionSearchCache } from "../../redux/slices/chat";
import { getEntityTypeDisplay } from "../../utils/entityDisplayUtils";
import { motion, AnimatePresence } from "framer-motion";

type TabTypes = "all" | "strategymap" | "epic" | "feature" | "team" | "resource";

type SuggestionItem = {
  id?: string | number;
  type: TabTypes | string;
  name?: string;
  email?: string;
  skills_name?: string[];
  projects?: string[];
  image_url?: string;
  modified_at?: string;
  entity_type?: string;
  api_type?: string;
};

const SuggestionIcon = ({ item }: { item: SuggestionItem }) => {
  if (item.type === "resource") {
    // Generate initials
    const initials = item.name
      ? item.name
        .split(" ")
        .map((word) => word[0])
        .slice(0, 2)
        .join("")
        .toUpperCase() || "?"
      : "?";

    // Determine background color based on initials
    const backgroundColors = [
      "from-blue-500 to-blue-600",
      "from-green-500 to-green-600",
      "from-purple-500 to-purple-600",
      "from-pink-500 to-pink-600",
      "from-indigo-500 to-indigo-600",
      "from-teal-500 to-teal-600",
    ];
    const colorIndex =
      initials.charCodeAt(0) % backgroundColors.length;

    return (
      <div className="flex-shrink-0">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name || "Resource"}
            className="w-9 h-9 rounded-lg object-cover ring-1 ring-gray-200 dark:ring-gray-600"
          />
        ) : (
          <CustomTooltip placement="top-start" title={item.name || ''}>
            <div
              className={`w-9 h-9 rounded-lg bg-gradient-to-br ${backgroundColors[colorIndex]} flex items-center justify-center text-white text-sm font-semibold shadow-sm`}
            >
              {initials}
            </div>
          </CustomTooltip>
        )}
      </div>
    );
  }

  if (item.type === "epic" || item.type === "feature") {
    const entityInfo = getEntityTypeDisplay(item.entity_type ?? "", item.api_type ?? "");
    const EntityIcon = entityInfo?.icon;
    
    return (
      <div className="flex-shrink-0">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shadow-sm ${entityInfo.bgClass || "bg-gray-100 dark:bg-gray-800"}`}>
          {EntityIcon ? (
             // @ts-ignore
            <EntityIcon className={`w-5 h-5 ${entityInfo.iconClass || "text-gray-600 dark:text-gray-400"}`} />
          ) : (
            <HiOutlineBriefcase className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          )}
        </div>
      </div>
    );
  }

  // Fallback for work_unit, team, strategymap
  let bgClass = "bg-purple-100 dark:bg-purple-900/30";
  let iconColor = "text-purple-600 dark:text-purple-400";
  let Icon = HiOutlineViewBoards;

  if (item.type === "work_unit") {
    bgClass = "bg-blue-100 dark:bg-blue-900/30";
    iconColor = "text-blue-600 dark:text-blue-400";
    Icon = HiOutlineBriefcase;
  } else if (item.type === "team") {
    bgClass = "bg-green-100 dark:bg-green-900/30";
    iconColor = "text-green-600 dark:text-green-400";
    Icon = HiOutlineUserGroup;
  }

  return (
    <div className="flex-shrink-0">
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center shadow-sm ${bgClass}`}
      >
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
    </div>
  );
};


type AtDropdownProps = {
  showDropdown: boolean;
  selectedSuggestionIndex: number;
  suggestions: SuggestionItem[] | null;
  loadingSearch: boolean;
  loadingMore?: boolean;
  handleRoadmapSelectInternal: (
    item: SuggestionItem,
    options?: { fromButton?: boolean }
  ) => void;
  dropdownRef: React.RefObject<HTMLDivElement>;
  isDropdownFromButton?: boolean;
  textareaRef?: React.RefObject<HTMLTextAreaElement> | null;
  hasMoreAll?: boolean;
  hasMoreEpics?: boolean;
  hasMoreFeatures?: boolean;
  hasMoreStrategyMaps?: boolean;
  hasMoreTeams?: boolean;
  hasMoreResources?: boolean;
  onLoadMore?: () => void;
  preferBelow?: boolean;
  onBackendSearch?: (term: string, searchType: MentionSearchType) => void;
  initialSearchType?: MentionSearchType;
  currentSearchTerm?: string;
};

const getTabFromSearchType = (
  searchType: MentionSearchType
): TabTypes => {
  switch (searchType) {
    case MentionSearchType.PROJECT:
      return "epic";
    case MentionSearchType.ROADMAP:
      return "strategymap";
    case MentionSearchType.TEAM:
      return "team";
    case MentionSearchType.RESOURCE:
      return "resource";
    default:
      return "all";
  }
};

const AtDropdown: React.FC<AtDropdownProps> = ({
  showDropdown,
  selectedSuggestionIndex,
  suggestions,
  loadingSearch,
  loadingMore,
  handleRoadmapSelectInternal,
  dropdownRef,
  isDropdownFromButton = false,
  textareaRef = null,
  hasMoreAll = false,
  hasMoreEpics = false,
  hasMoreFeatures = false,
  hasMoreStrategyMaps = false,
  hasMoreTeams = false,
  hasMoreResources = false,
  preferBelow = false,
  onLoadMore,
  onBackendSearch,
  initialSearchType = MentionSearchType.ALL,
  currentSearchTerm = '',
}) => {
  const {
    isNewChat,
    showBigChatbox
  } = useSelector((state: any) => state.chatMessages);
  const dispatch = useDispatch();

  const [searchValue, setSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState<TabTypes>("all");
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const calculatePositionWidthAndHeight = () => {
    if (!textareaRef?.current) {
      return { position: { x: 0, y: 0 }, width: 320, maxHeight: 280 };
    }
    const wrapper = textareaRef.current.parentElement;
    const rect = wrapper
      ? wrapper.getBoundingClientRect()
      : textareaRef.current.getBoundingClientRect();

    const width = Math.max(Math.round(rect.width), 320); // Minimum 320px

    const gap = 12;
    const minHeight = 180;
    const maxHeightLimit = showBigChatbox ? 300 : 300;
    const paddingBuffer = 40;

    const chatboxContainer = (textareaRef.current.closest('[class*="chatbox"]')
      || textareaRef.current.closest('[class*="chat-interface"]')
      || textareaRef.current.closest('[class*="modal"]')
      || document.querySelector(".modal-content")
      || document.body);
    const containerRect = chatboxContainer.getBoundingClientRect();

    let position: { x: number; y: number };
    let maxHeight: number;

    if (preferBelow || (showBigChatbox && isNewChat)) {
      position = {
        x: rect.left,
        y: rect.bottom + gap,
      };

      const availableSpaceBelow =
        containerRect.bottom - rect.bottom - gap - paddingBuffer;
      maxHeight = Math.min(
        Math.max(availableSpaceBelow, minHeight),
        maxHeightLimit
      );
    } else {
      const availableSpaceAbove =
        rect.top - containerRect.top - gap - paddingBuffer;
      maxHeight = Math.min(
        Math.max(availableSpaceAbove, minHeight),
        maxHeightLimit
      );

      position = {
        x: rect.left,
        y: rect.top - maxHeight - gap,
      };
    }

    return { position, width, maxHeight };
  };
  const {
    position: dropdownPosition,
    width: dropdownWidth,
    maxHeight: dropdownMaxHeight,
  } = showDropdown
      ? calculatePositionWidthAndHeight()
      : { position: { x: 0, y: 0 }, width: 320, maxHeight: 280 };

  useEffect(() => {
    if (showDropdown) {
      dispatch(clearMentionSearchCache());
      const hasContextSearch = initialSearchType !== MentionSearchType.ALL || (currentSearchTerm && currentSearchTerm.trim() !== "");
      const nextTab = hasContextSearch ? getTabFromSearchType(initialSearchType) : "all";
      const nextTerm = hasContextSearch ? (currentSearchTerm || "") : "";

      setActiveTab(nextTab);
      setSearchValue(nextTerm);

      if (onBackendSearch) {
        const searchType = getSearchTypeFromTab(nextTab);
        onBackendSearch(nextTerm, searchType);
      }
    } else {
      setSearchValue("");
      setActiveTab("all");
    }
  }, [showDropdown]);

  useEffect(() => {
    if (showDropdown && currentSearchTerm !== searchValue) {
      setSearchValue(currentSearchTerm);
    }
  }, [currentSearchTerm, showDropdown]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  const getSearchTypeFromTab = (
    tab: TabTypes
  ): MentionSearchType => {
    switch (tab) {
      case "all":
        return MentionSearchType.ALL;
      case "strategymap":
        return MentionSearchType.STRATEGY_MAP;
      case "epic":
        return MentionSearchType.EPIC;
      case "feature":
        return MentionSearchType.FEATURE;
      case "team":
        return MentionSearchType.TEAM;
      case "resource":
        return MentionSearchType.RESOURCE;
      default:
        return MentionSearchType.ALL;
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchValue(val);
    if (!onBackendSearch) return;
    const searchType = getSearchTypeFromTab(activeTab);
    onBackendSearch(val.trim() === "" ? "" : val, searchType);
  };

  const handleTabChange = (
    tab: TabTypes,
  ) => {
    setActiveTab(tab);
    const tabElement = tabRefs.current[tab];
    if (tabElement && tabsContainerRef.current) {
      tabElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
    if (onBackendSearch) {
      const searchType = getSearchTypeFromTab(tab);
      onBackendSearch(searchValue, searchType);
    }
  };

  const tabs: {
    id: TabTypes;
    label: string;
    icon: any;
    children?: {
      id: "epic" | "feature";
      label: string;
    }[];
  }[] = [
    { id: "all", label: "All", icon: null },
    { id: "strategymap", label: "Strategy Maps", icon: HiOutlineViewBoards },
    {
      id: "epic",
      label: "Work Items",
      icon: HiOutlineBriefcase,
      children: [
        {
          id: "epic",
          label: "Epic",
        },
        {
          id: "feature",
          label: "Feature",
        },
      ],
    },
    { id: "team", label: "Teams", icon: HiOutlineUserGroup },
    { id: "resource", label: "Resources", icon: HiOutlineUser },
  ];

  const getFilteredItems = useMemo(() => {
    let items: SuggestionItem[] = suggestions || [];
    if (activeTab !== "all") {
      items = items.filter((item) => item.type === activeTab);
    }
    if (searchValue && searchValue.trim() !== "") {
      const term = searchValue.toLowerCase().trim();
      items = items.filter((item) => {
        if (item.name && item.name.toLowerCase().includes(term)) {
          return true;
        }
        if (item.email && item.email.toLowerCase().includes(term)) {
          return true;
        }
        if (item.skills_name && Array.isArray(item.skills_name)) {
          const skillsMatch = item.skills_name.some((skill) =>
            (skill || "").toLowerCase().includes(term)
          );
          if (skillsMatch) return true;
        }
        if (item.projects && Array.isArray(item.projects)) {
          const projectMatch = item.projects.some((project) =>
            (project || "").toLowerCase().includes(term)
          );
          if (projectMatch) return true;
        }
        return false;
      });
    }

    return items;
  }, [suggestions, activeTab, searchValue]);

  const filteredSuggestions = getFilteredItems;

  // Determine pagination eligibility based on active tab
  const allowPagination = useMemo(() => {
    if (activeTab === "all") return hasMoreAll;
    if (activeTab === "epic") return hasMoreEpics;
    if (activeTab === "feature") return hasMoreFeatures;
    if (activeTab === "strategymap") return hasMoreStrategyMaps;
    if (activeTab === "team") return hasMoreTeams;
    if (activeTab === "resource") return hasMoreResources;
    return false;
  }, [
    activeTab,
    hasMoreAll,
    hasMoreEpics,
    hasMoreFeatures,
    hasMoreStrategyMaps,
    hasMoreTeams,
    hasMoreResources,
  ]);

  // IntersectionObserver to auto-load more
  useEffect(() => {
    if (!showDropdown) return;
    if (!allowPagination || !onLoadMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const rootEl = dropdownRef?.current
      ? (dropdownRef.current.querySelector('[data-scroll-container="true"]'))
      : null;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && allowPagination && !loadingMore) {
            onLoadMore();
          }
        });
      },
      {
        root: rootEl || undefined,
        threshold: 0.1,
        rootMargin: "0px 0px 200px 0px",
      }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    allowPagination,
    onLoadMore,
    loadingMore,
    showDropdown,
    dropdownRef,
    activeTab,
  ]);

  return (
    <>
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="fixed z-50 bg-white dark:bg-secondary-dark border border-gray-200 dark:border-gray-700 rounded-2xl  overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200 flex flex-col"
          style={{
            left: `${dropdownPosition.x}px`,
            top: `${dropdownPosition.y}px`,
            width: `${dropdownWidth}px`,
            height: `${dropdownMaxHeight}px`,
          }}
        >
          {!showBigChatbox && (
            <div className="flex-shrink-0 p-0.5 pl-1 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-secondary-dark flex items-center">
              <div className="relative w-full">
                <HiMagnifyingGlass className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Search name, email, skills, teams..."
                  className="w-full pl-8 pr-8 py-1.5 text-sm border-none rounded-md outline-none focus:outline-none focus:ring-0 shadow-inner dark:bg-secondary-dark text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors placeholder:text-xs flex items-center"
                />
                {searchValue && (
                  <button
                    onClick={() => {
                      setSearchValue("");
                      if (onBackendSearch) {
                        const searchType = getSearchTypeFromTab(activeTab);
                        onBackendSearch("", searchType);
                      }
                    }}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label="Clear search"
                  >
                    <HiXMark className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          <div
            className={`flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary-dark ${showBigChatbox ? "px-3 py-2" : "px-2 py-1.5"
              }`}
          >
            <div
              className={`flex items-center ${showBigChatbox ? "justify-between gap-3" : "flex-wrap gap-2"
                }`}
            >
              <div
                ref={tabsContainerRef}
                className={`flex gap-1 overflow-x-auto no-scrollbar`}
                style={{ scrollBehavior: "smooth" }}
              >
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  const Icon = tab.icon;
                  const shouldShowChild = (tab.children?.length ?? 0) > 0 ? tab.children?.reduce((pv, t)=>pv || t.id === activeTab, false) : false;
                  return (
                    <button
                      ref={(el) => {
                        tabRefs.current[tab.id] = el;
                      }}
                      key={tab.id}
                      onClick={() => {
                        return tab.children ? !shouldShowChild && handleTabChange(tab.id) : handleTabChange(tab.id);
                      }}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded-lg shadow-inner text-xs font-medium transition-all duration-150 whitespace-nowrap ${
                        isActive
                          ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 shadow-sm"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50"
                      }`}
                    >
                      {Icon && <Icon className="w-3.5 h-3.5" />}

                      <span>{tab.label}</span>

                      {(tab.children?.length ?? 0) > 0 && shouldShowChild && (
                        <motion.div 
                          className="flex gap-1"
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                        >
                          {tab.children?.map((child) => {
                            const isActiveChild = activeTab === child.id;
                        
                            return (
                              <button
                                key={child.id}
                                onClick={() => {
                                  handleTabChange(child.id);
                                }}
                                className={`relative flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-colors duration-150 whitespace-nowrap z-0 ${
                                  isActiveChild
                                    ? "text-white dark:text-blue-300 shadow-sm"
                                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50"
                                }`}
                              >
                                {isActiveChild && (
                                  <motion.div
                                    layoutId="activeChildTab"
                                    className="absolute inset-0 bg-blue-800 dark:bg-blue-900/30 rounded-lg -z-10 shadow-sm"
                                    initial={false}
                                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                  />
                                )}
                                <span>{child.label}</span>
                              </button>
                            );
                          })}
                        </motion.div>
                      )}
                    </button>
                  );
                })}
              </div>
              {showBigChatbox && (
                <div
                  className="relative flex-shrink-0 ml-auto flex items-center"
                  style={{ maxWidth: "320px" }}
                >
                  <HiMagnifyingGlass className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    value={searchValue}
                    onChange={handleSearchChange}
                    placeholder="Search..."
                    className="w-full pl-8 pr-8 py-1 text-xs bg-white dark:bg-secondary-dark border border-gray-300 shadow-inner dark:border-gray-700 rounded-lg outline-none focus:outline-none focus:ring-0 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors flex items-center"
                  />
                  {searchValue && (
                    <button
                      onClick={() => {
                        setSearchValue("");
                        if (onBackendSearch) {
                          const searchType = getSearchTypeFromTab(activeTab);
                          onBackendSearch("", searchType);
                        }
                      }}
                      className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                      aria-label="Clear search"
                    >
                      <HiXMark className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto min-h-0"
            data-scroll-container="true"
          >
            {loadingSearch && filteredSuggestions.length === 0 ? (
              <div className="p-4 space-y-3 h-full">
                {[1, 2, 3, 4, 5].map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-3 animate-pulse"
                  >
                    <div className="w-9 h-9 bg-gray-300 dark:bg-gray-700 rounded-lg"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredSuggestions.length > 0 ? (
              <div className="py-1">
                {filteredSuggestions.map((item, index) => {
                  return (
                    <button
                      key={`${item.id}-${item.type}-${index}-${item.name}`}
                      data-suggestion-index={index}
                      onClick={() => {
                        const clickOptions = { fromButton: isDropdownFromButton };
                        handleRoadmapSelectInternal(item, clickOptions);
                      } }
                      className={`w-full text-left px-3 py-2.5 transition-all duration-150 flex items-center gap-3 ${selectedSuggestionIndex === index
                        ? "bg-blue-50 dark:bg-blue-900/20 border-l-3 border-blue-500"
                        : "hover:bg-gray-50 dark:hover:bg-gray-700/30"}`}
                    >
                      <SuggestionIcon item={item} />

                      <div className="flex-1 min-w-0">
                        <CustomTooltip placement="top-start" title={`${item.name}${item.email ? ` (${item.email})` : ""}`}>
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {item.name}
                            {item.email && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 font-normal ml-1.5">
                                ({item.email})
                              </span>
                            )}
                          </div>
                        </CustomTooltip>

                        {/* Resource Skills */}
                        {item.skills_name && item.skills_name.length > 0 && (
                          <CustomTooltip placement="top-start" title={item.skills_name.join(", ")}>
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                              {item.skills_name.slice(0, 3).join(", ")}
                            </div>
                          </CustomTooltip>
                        )}

                        {/* Team Projects */}
                        {item.type === "team" && item.projects && item.projects.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {item.projects
                              .slice(0, 2)
                              .map((project, idx) => (
                                <CustomTooltip placement="top-start" key={idx} title={project}>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 truncate max-w-[120px]">
                                    {project}
                                  </span>
                                </CustomTooltip>
                              ))}
                            {item.projects.length > 2 && (
                              <CustomTooltip placement="top-start" title={`${item.projects.length - 2} more projects: ${item.projects.slice(2).join(", ")}`}>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                  +{item.projects.length - 2}
                                </span>
                              </CustomTooltip>
                            )}
                          </div>
                        )}

                        {/* Labels */}
                        {item.type === "work_unit" && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Work Items
                          </div>
                        )}
                        {(item.type === "strategy_map" || item.type === "strategymap") && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Strategy Map
                          </div>
                        )}
                        {item.type === "epic" && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Epic
                          </div>
                        )}
                        {item.type === "feature" && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Feature
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
                {allowPagination && (
                  <div
                    className="w-full flex items-center justify-center p-2"
                    data-sentinel="true"
                    ref={sentinelRef}
                  >
                    {loadingMore && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Loading…
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full min-h-[180px]">
                <div className="text-center">
                  <div className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                    No results found
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {searchValue
                      ? "Try a different search term"
                      : "No items available"}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AtDropdown;